<template>
  <div v-if="visible">
    <!-- Desktop: floating pill + card -->
    <div v-if="!isMobile" class="fb-desktop">
      <button v-if="!expanded" class="fb-pill" @click="expanded = true">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
        Feedback
      </button>

      <div v-if="expanded" class="fb-card">
        <button class="fb-close" @click="expanded = false">&times;</button>
        <template v-if="submitted">
          <div class="fb-done">
            <div class="fb-done-check">✓</div>
            <p class="fb-done-title">Thank you!</p>
            <p class="fb-done-sub">Your feedback helps us improve Renants.</p>
            <div class="fb-done-btns">
              <button class="fb-done-btn fb-done-btn-primary" @click="resetForm">Send another</button>
              <button class="fb-done-btn fb-done-btn-ghost" @click="dismissForDays">Done</button>
            </div>
          </div>
        </template>
        <template v-else>
          <p class="fb-heading">How's your experience?</p>
          <div class="fb-stars">
            <button v-for="n in 5" :key="n" class="fb-star" :class="{ active: hoverStar >= n || selectedStar >= n }" @mouseenter="hoverStar = n" @mouseleave="hoverStar = 0" @click="selectedStar = n">★</button>
          </div>
          <p class="fb-star-label">{{ starLabel }}</p>
          <textarea v-model="message" class="fb-text" placeholder="Any suggestions? (optional)" rows="2" maxlength="500" />
          <button class="fb-send" :disabled="!selectedStar && !message.trim()" @click="submit">
            {{ submitting ? 'Sending...' : 'Send' }}
          </button>
          <p v-if="error" class="fb-error">{{ error }}</p>
        </template>
      </div>
    </div>

    <!-- Mobile: bottom sheet -->
    <div v-if="isMobile && expanded" class="fb-overlay" @click.self="expanded = false">
      <div class="fb-sheet">
        <div class="fb-sheet-bar" />
        <template v-if="submitted">
          <div class="fb-done">
            <p class="fb-done-title">✓ Thanks for your feedback!</p>
            <div class="fb-done-btns">
              <button class="fb-link" @click="resetForm">Send another</button>
              <button class="fb-link fb-link-muted" @click="dismissForDays">Done</button>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="fb-sheet-head">
            <span class="fb-heading">How's your experience?</span>
            <button class="fb-close-sm" @click="expanded = false">&times;</button>
          </div>
          <div class="fb-stars">
            <button v-for="n in 5" :key="n" class="fb-star" :class="{ active: hoverStar >= n || selectedStar >= n }" @click="selectedStar = n">★</button>
          </div>
          <p class="fb-star-label">{{ starLabel }}</p>
          <textarea v-model="message" class="fb-text" placeholder="Any suggestions? (optional)" rows="2" maxlength="500" />
          <button class="fb-send" :disabled="!selectedStar && !message.trim()" @click="submit">
            {{ submitting ? 'Sending...' : 'Send' }}
          </button>
          <p v-if="error" class="fb-error">{{ error }}</p>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const DISMISS_KEY = 'feedback_dismissed_at';
const DISMISS_DAYS = 1;
const STAR_LABELS = ['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'];

export default {
  name: 'FeedbackBubble',
  data() {
    return {
      visible: false,
      expanded: false,
      submitted: false,
      submitting: false,
      error: '',
      selectedStar: 0,
      hoverStar: 0,
      message: '',
      isMobile: window.innerWidth <= 600,
    };
  },
  computed: {
    starLabel() {
      const s = this.hoverStar || this.selectedStar;
      return STAR_LABELS[s] || '';
    },
    ratingValue() {
      const s = this.selectedStar;
      if (s >= 4) return 'happy';
      if (s === 3) return 'neutral';
      if (s >= 1) return 'sad';
      return null;
    },
  },
  mounted() {
    window.addEventListener('resize', this.checkMobile);
    this.visible = true;
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
  },
  methods: {
    checkMobile() { this.isMobile = window.innerWidth <= 600; },
    async submit() {
      if (this.submitting) return;
      this.error = '';
      this.submitting = true;
      try {
        const token = localStorage.getItem('auth-token');
        const headers = {};
        if (token) headers.Authorization = `Bearer ${token}`;
        await axios.post('/api/feedback', {
          rating: this.ratingValue,
          message: this.message.trim(),
          stars: this.selectedStar,
          page: window.location.pathname,
        }, { headers });
        this.submitted = true;
      } catch { this.error = 'Failed to send. Try again.'; }
      finally { this.submitting = false; }
    },
    resetForm() {
      this.submitted = false;
      this.selectedStar = 0;
      this.hoverStar = 0;
      this.message = '';
      this.error = '';
    },
    dismissForDays() {
      localStorage.setItem(DISMISS_KEY, Date.now().toString());
      this.expanded = false;
    },
  },
};
</script>

<style scoped>
/* ── Desktop wrapper ── */
.fb-desktop {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  font-family: 'Inter', -apple-system, sans-serif;
}

/* ── Pill ── */
.fb-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 10px 20px;
  background: linear-gradient(135deg, #3467D5 0%, #1e3a8a 100%);
  color: #fff;
  border: none;
  border-radius: 28px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(30, 58, 138, 0.35);
  transition: all 0.25s ease;
}
.fb-pill svg { flex-shrink: 0; }
.fb-pill:hover { transform: translateY(-3px); box-shadow: 0 8px 28px rgba(30, 58, 138, 0.45); }

/* ── Card ── */
.fb-card {
  width: 300px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  padding: 20px;
  position: relative;
  animation: fb-in 0.2s ease-out;
}
@keyframes fb-in {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

.fb-close {
  position: absolute; top: 12px; right: 12px;
  width: 26px; height: 26px; border-radius: 50%;
  background: #f3f4f6; border: none; font-size: 16px;
  color: #888; cursor: pointer; display: flex;
  align-items: center; justify-content: center;
}
.fb-close:hover { background: #e5e7eb; }

/* ── Heading ── */
.fb-heading {
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px;
}

/* ── Stars ── */
.fb-stars {
  display: flex;
  gap: 4px;
  margin-bottom: 4px;
}
.fb-star {
  font-size: 28px;
  background: none;
  border: none;
  cursor: pointer;
  color: #d1d5db;
  transition: color 0.1s, transform 0.1s;
  padding: 0 2px;
  line-height: 1;
  -webkit-tap-highlight-color: transparent;
}
.fb-star:hover { transform: scale(1.15); }
.fb-star.active { color: #f59e0b; }

.fb-star-label {
  font-size: 12px;
  color: #f59e0b;
  font-weight: 600;
  height: 16px;
  margin: 0 0 10px;
}

/* ── Textarea ── */
.fb-text {
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
  font-family: inherit;
  resize: none;
  box-sizing: border-box;
  color: #1f2937;
  -webkit-appearance: none;
  margin-bottom: 12px;
}
.fb-text:focus { outline: none; border-color: #3467D5; }

/* ── Send ── */
.fb-send {
  width: 100%;
  padding: 10px 0;
  background: #3467D5;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  -webkit-appearance: none;
}
.fb-send:hover:not(:disabled) { background: #2851a3; }
.fb-send:disabled { opacity: 0.4; cursor: not-allowed; }

.fb-error { color: #ef4444; font-size: 12px; margin: 6px 0 0; text-align: center; }

/* ── Done ── */
.fb-done { text-align: center; padding: 12px 0 4px; }
.fb-done-check {
  width: 48px; height: 48px; border-radius: 50%;
  background: linear-gradient(135deg, #3467D5, #1e3a8a);
  color: #fff; font-size: 24px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 12px;
  animation: fb-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes fb-check-pop {
  from { transform: scale(0); }
  to { transform: scale(1); }
}
.fb-done-title { font-size: 18px; font-weight: 700; color: #1f2937; margin: 0 0 4px; }
.fb-done-sub { font-size: 13px; color: #888; margin: 0 0 16px; }
.fb-done-btns { display: flex; gap: 8px; justify-content: center; }
.fb-done-btn {
  padding: 9px 20px; border: none; border-radius: 10px;
  font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer;
}
.fb-done-btn-primary { background: #3467D5; color: #fff; }
.fb-done-btn-primary:hover { background: #2851a3; }
.fb-done-btn-ghost { background: #f3f4f6; color: #555; }
.fb-done-btn-ghost:hover { background: #e5e7eb; }

/* ══════ Mobile bottom sheet ══════ */
.fb-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.35);
  z-index: 10000;
  display: flex; align-items: flex-end;
  font-family: 'Inter', -apple-system, sans-serif;
}
.fb-sheet {
  width: 100%;
  background: #fff;
  border-radius: 16px 16px 0 0;
  padding: 6px 20px calc(20px + env(safe-area-inset-bottom, 0px));
  box-shadow: 0 -4px 24px rgba(0,0,0,0.1);
  animation: fb-up 0.25s ease-out;
}
@keyframes fb-up {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}
.fb-sheet-bar {
  width: 32px; height: 4px;
  background: #d1d5db; border-radius: 2px;
  margin: 0 auto 10px;
}
.fb-sheet-head {
  display: flex; align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.fb-close-sm {
  width: 28px; height: 28px; border-radius: 50%;
  background: #f3f4f6; border: none; font-size: 18px;
  color: #888; cursor: pointer; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}
</style>
